import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { ExistingPatientConflict, existingPatientConflictTranslationMapping } from "@/models/ExistingPatientModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { ColoredCard } from "@/uiKit/atoms/ColoredCard.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type ExistingPatientCardProps = {
  status: ExistingPatientConflict;
  /** Only provided when the patient is already monitored by the current doctor. */
  onGoToPatient?: () => void;
  className?: string;
};

export const ExistingPatientCard: React.FC<ExistingPatientCardProps> = ({ status, onGoToPatient, className }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <ColoredCard square color="red" className={className}>
      <CardContent>
        <Typography variant="subtitle" component="h6" className={styles.title}>
          {t("diabetesForms.existingPatient.title")}
        </Typography>
        <Typography variant="body">{t(existingPatientConflictTranslationMapping(status))}</Typography>
      </CardContent>
      {onGoToPatient && (
        <CardFooter position="start">
          <Button
            id="existing-patient-go-to-patient"
            variant="contained"
            data-action="patient-creation.existing-patient.navigate"
            onClick={onGoToPatient}
          >
            {t("diabetesForms.existingPatient.goToPatient")}
          </Button>
        </CardFooter>
      )}
    </ColoredCard>
  );
};

const makeStyles = (theme: Theme) => ({
  title: css({
    marginBottom: theme.spacing(4),
  }),
});
